// Performance budget definitions and monitoring
export const PERFORMANCE_BUDGETS = {
  lite: {
    html: 30 * 1024, // 30 KB
    css: 25 * 1024,  // 25 KB
    js: 80 * 1024,   // 80 KB
    images: 250 * 1024, // 250 KB on screen
    fcp: 1500,       // 1.5s First Contentful Paint
    lcp: 2500,       // 2.5s Largest Contentful Paint
    tbt: 150,        // 150ms Total Blocking Time
    cls: 0.05        // 0.05 Cumulative Layout Shift
  },
  full: {
    html: 50 * 1024,  // 50 KB (more generous)
    css: 40 * 1024,   // 40 KB
    js: 250 * 1024,   // 250 KB max
    images: 500 * 1024, // 500 KB on screen
    fcp: 2000,        // 2s
    lcp: 3000,        // 3s
    tbt: 300,         // 300ms
    cls: 0.1          // 0.1
  }
} as const;

export type BudgetType = keyof typeof PERFORMANCE_BUDGETS;
export type MetricType = keyof typeof PERFORMANCE_BUDGETS.lite;

// Performance metrics collection
export class PerformanceMonitor {
  private metrics: Map<string, number> = new Map();
  private budgetType: BudgetType;
  private observers: PerformanceObserver[] = [];

  constructor(budgetType: BudgetType = 'lite') {
    this.budgetType = budgetType;
    this.initializeObservers();
  }

  private initializeObservers() {
    if (typeof window === 'undefined') return;

    // Web Vitals Observer
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          switch (entry.entryType) {
            case 'paint':
              if (entry.name === 'first-contentful-paint') {
                this.metrics.set('fcp', entry.startTime);
              }
              break;
            case 'largest-contentful-paint':
              this.metrics.set('lcp', entry.startTime);
              break;
            case 'layout-shift':
              const cls = this.metrics.get('cls') || 0;
              this.metrics.set('cls', cls + (entry as any).value);
              break;
          }
        }
      });

      observer.observe({ entryTypes: ['paint', 'largest-contentful-paint', 'layout-shift'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('Performance Observer not supported:', error);
    }

    // Monitor Total Blocking Time approximation
    this.measureTBT();
  }

  private measureTBT() {
    if (typeof window === 'undefined') return;
    
    let tbt = 0;
    const startTime = performance.now();
    
    const measureTask = () => {
      const taskStart = performance.now();
      setTimeout(() => {
        const taskEnd = performance.now();
        const taskDuration = taskEnd - taskStart;
        if (taskDuration > 50) {
          tbt += taskDuration - 50;
        }
        this.metrics.set('tbt', tbt);
        
        // Continue measuring for first 5 seconds
        if (taskEnd - startTime < 5000) {
          measureTask();
        }
      }, 0);
    };
    
    measureTask();
  }

  // Resource size monitoring
  async measureResourceSizes(): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      
      let cssSize = 0;
      let jsSize = 0;
      let imageSize = 0;

      for (const entry of entries) {
        const size = entry.transferSize || entry.decodedBodySize || 0;
        
        if (entry.name.includes('.css') || entry.initiatorType === 'link') {
          cssSize += size;
        } else if (entry.name.includes('.js') || entry.initiatorType === 'script') {
          jsSize += size;
        } else if (entry.initiatorType === 'img' || entry.name.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
          imageSize += size;
        }
      }

      // Add main document size as HTML
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const htmlSize = navigationEntries[0]?.transferSize || 0;

      this.metrics.set('html', htmlSize);
      this.metrics.set('css', cssSize);
      this.metrics.set('js', jsSize);
      this.metrics.set('images', imageSize);
    } catch (error) {
      console.warn('Failed to measure resource sizes:', error);
    }
  }

  // Check if metrics are within budget
  checkBudgets(): { metric: MetricType; actual: number; budget: number; over: boolean }[] {
    const budget = PERFORMANCE_BUDGETS[this.budgetType];
    const results: { metric: MetricType; actual: number; budget: number; over: boolean }[] = [];

    for (const [metricKey, budgetValue] of Object.entries(budget)) {
      const metric = metricKey as MetricType;
      const actual = this.metrics.get(metric) || 0;
      const over = actual > budgetValue;
      
      results.push({
        metric,
        actual,
        budget: budgetValue,
        over
      });
    }

    return results;
  }

  // Get current metrics
  getMetrics(): Record<MetricType, number> {
    const budget = PERFORMANCE_BUDGETS[this.budgetType];
    const result = {} as Record<MetricType, number>;
    
    for (const metric of Object.keys(budget) as MetricType[]) {
      result[metric] = this.metrics.get(metric) || 0;
    }
    
    return result;
  }

  // Performance score (0-100)
  getPerformanceScore(): number {
    const results = this.checkBudgets();
    const totalChecks = results.length;
    const passedChecks = results.filter(r => !r.over).length;
    
    return Math.round((passedChecks / totalChecks) * 100);
  }

  // Clean up observers
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics.clear();
  }

  // Format size in human readable format
  static formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  // Format time in human readable format
  static formatTime(ms: number): string {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }
}

// Global performance monitor instance
let globalMonitor: PerformanceMonitor | null = null;

export const initializePerformanceMonitor = (budgetType: BudgetType = 'lite'): PerformanceMonitor => {
  if (globalMonitor) {
    globalMonitor.destroy();
  }
  
  globalMonitor = new PerformanceMonitor(budgetType);
  
  // Measure resource sizes after page load
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      setTimeout(() => globalMonitor?.measureResourceSizes(), 1000);
    });
  }
  
  return globalMonitor;
};

export const getPerformanceMonitor = (): PerformanceMonitor | null => globalMonitor;

// Performance budget enforcement
export const enforceBudget = (actual: number, budget: number, metric: string): boolean => {
  const exceeded = actual > budget;
  
  if (exceeded) {
    console.warn(`⚠️ Performance Budget Exceeded:`, {
      metric,
      actual: actual > 1000 ? PerformanceMonitor.formatSize(actual) : PerformanceMonitor.formatTime(actual),
      budget: budget > 1000 ? PerformanceMonitor.formatSize(budget) : PerformanceMonitor.formatTime(budget),
      overage: `${((actual / budget - 1) * 100).toFixed(1)}%`
    });
  }
  
  return !exceeded;
};

// Early warning system
export const checkEarlyWarning = (actual: number, budget: number, threshold = 0.8): boolean => {
  return actual > (budget * threshold);
};